import React from 'react';
import {Col, Row, Space, Tag, Typography} from 'antd';
import {EnvironmentOutlined, ShoppingOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {moneyMask, dateFormat} from '../../utils/utils';
import {BORDER_GRAY, GREE_1, WHITE, ORDER_STATUS_COLOR} from '../../utils/colors';
import {formatPath, ROUTES} from '../../constants/PathRoutes';

const CardWrapper = styled.div`
    background: ${WHITE};
    border: 1px solid ${BORDER_GRAY};
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 16px;
    cursor: pointer;
    transition: box-shadow 0.2s;
    &:hover {
        box-shadow: 0 2px 12px rgba(0,0,0,0.10);
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    background: #fafafa;
    border-bottom: 1px solid ${BORDER_GRAY};
    flex-wrap: wrap;
    gap: 8px;
`;

const Body = styled.div`
    padding: 16px 20px;
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 14px 20px;
    border-top: 1px solid ${BORDER_GRAY};
    background: #fafafa;
    flex-wrap: wrap;
    gap: 12px;
`;

const ItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
`;

const ItemImage = styled.img`
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
`;

const ImagePlaceholder = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 6px;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #ccc;
    font-size: 18px;
`;

const VISIBLE_ITEMS = 2;

export default function OrderCard({order}) {
    const navigate = useNavigate();
    const hasDiscount = order.discount_total && parseFloat(order.discount_total) > 0;
    const address = [
        `${order.delivery_street}, ${order.delivery_number}`,
        order.delivery_complement || null,
        `${order.delivery_neighborhood} — ${order.delivery_city}/${order.delivery_state}`,
        `CEP ${order.delivery_postal_code}`,
    ].filter(Boolean).join(' · ');

    return (
        <CardWrapper onClick={() => navigate(formatPath(ROUTES.LOJA_PEDIDO_DETAIL, {id: order.id}))}>
            <Header>
                <Space direction="vertical" size={2}>
                    <Typography.Text strong style={{fontSize: 13}}>
                        {order.order_number}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{fontSize: 12}}>
                        {dateFormat(order.created_at)}
                    </Typography.Text>
                </Space>
                <Tag color={ORDER_STATUS_COLOR[order.status] ?? 'default'}>
                    {order.status_display}
                </Tag>
            </Header>

            <Body>
                {order.items.slice(0, VISIBLE_ITEMS).map(item => (
                    <ItemRow key={item.id}>
                        {item.product_image
                            ? <ItemImage src={item.product_image} alt={item.product_name}/>
                            : <ImagePlaceholder><ShoppingOutlined/></ImagePlaceholder>
                        }
                        <div style={{flex: 1, minWidth: 0}}>
                            <Typography.Text strong ellipsis style={{display: 'block'}}>
                                {item.product_name}
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{fontSize: 12}}>
                                {item.quantity}x · R$ {moneyMask(item.unit_price)}
                                {item.discount_percentage && (
                                    <span style={{color: '#fa8c16', marginLeft: 6}}>
                                        -{item.discount_percentage}% receita
                                    </span>
                                )}
                            </Typography.Text>
                        </div>
                        <Typography.Text strong style={{color: GREE_1, whiteSpace: 'nowrap'}}>
                            R$ {moneyMask(item.subtotal)}
                        </Typography.Text>
                    </ItemRow>
                ))}
                {order.items.length > VISIBLE_ITEMS && (
                    <Typography.Text type="secondary" style={{fontSize: 12, display: 'block', marginTop: 8}}>
                        + {order.items.length - VISIBLE_ITEMS} {order.items.length - VISIBLE_ITEMS === 1 ? 'item' : 'itens'}
                    </Typography.Text>
                )}
            </Body>

            <Footer>
                <Space size={4}>
                    <EnvironmentOutlined style={{color: '#999', fontSize: 12}}/>
                    <Typography.Text type="secondary" style={{fontSize: 12}}>
                        {address}
                    </Typography.Text>
                </Space>
                <Space direction="vertical" size={2} style={{alignItems: 'flex-end'}}>
                    {hasDiscount && (
                        <Row gutter={16} style={{margin: 0}}>
                            <Col>
                                <Typography.Text style={{fontSize: 12, color: '#999'}}>
                                    Subtotal: R$ {moneyMask(order.subtotal)}
                                </Typography.Text>
                            </Col>
                            <Col>
                                <Typography.Text style={{fontSize: 12, color: '#cf1322'}}>
                                    Desconto: -R$ {moneyMask(order.discount_total)}
                                </Typography.Text>
                            </Col>
                        </Row>
                    )}
                    <Typography.Text style={{fontSize: 12, color: '#666'}}>
                        Frete: R$ {moneyMask(order.shipping_cost)}
                    </Typography.Text>
                    <Typography.Text strong style={{fontSize: 15, color: GREE_1}}>
                        Total: R$ {moneyMask(order.total)}
                    </Typography.Text>
                </Space>
            </Footer>
        </CardWrapper>
    );
}
